/**
 * ImpersonationBanner (US-AE11.F3-0039-S03 · @UC9102) — banner CẢNH BÁO bền vững suốt phiên "xem như
 * user". Render ở composition root (App.tsx) khi có phiên active. Sticky top, màu cảnh báo semantic
 * (amber — không chỉ trang trí), role="alert" (a11y screen reader đọc ngay).
 *
 * Hiển thị: target user id + đếm ngược thời gian còn lại (tự cập nhật mỗi giây) + nút "Thoát chế độ".
 * Tự ẩn khi: phiên hết hạn (server lazy-expire → session null qua poll) HOẶC admin bấm Thoát.
 *
 * Invariants (S03):
 * - Banner bền suốt phiên (không ẩn vô tình) — chỉ ẩn khi expired/ended.
 * - Defence-in-depth UX: banner nhắc admin đang ở chế độ chỉ-đọc (backend là nguồn chặn ghi chính — S01).
 * - KHÔNG lộ token/PII thô — chỉ id target + thời gian.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useImpersonationSession, useEndImpersonation } from "./use-impersonation.js";

/** Số ms còn lại tới expiresAt (>=0). */
function remainingMs(expiresAtIso: string, nowMs: number): number {
  const exp = new Date(expiresAtIso).getTime();
  if (Number.isNaN(exp)) return 0;
  return Math.max(0, exp - nowMs);
}

/** Format mm:ss cho đếm ngược. */
function formatCountdown(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

export function ImpersonationBanner() {
  const { data } = useImpersonationSession();
  const end = useEndImpersonation();
  const [nowMs, setNowMs] = useState(() => Date.now());

  const session = data?.session ?? null;

  // Tick mỗi giây để đếm ngược cập nhật (chỉ khi có phiên — cleanup khi ẩn).
  useEffect(() => {
    if (!session) return;
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [session]);

  // Không có phiên active → banner ẩn (bền suốt phiên; chỉ ẩn khi expired/ended).
  if (!session) return null;

  const remaining = remainingMs(session.expiresAt, nowMs);

  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-3 border-b border-tone-warning-bd bg-tone-warning-bg px-4 py-2 text-tone-warning-fg"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="font-semibold uppercase tracking-wide">Chế độ xem-như-user (chỉ đọc)</span>
        <span className="text-tone-warning-fg opacity-90">
          Đang xem như người dùng <span className="font-medium">{session.targetUserId}</span> — mọi
          hành động ghi bị chặn.
        </span>
        <span
          className="tabular-nums font-medium"
          aria-label={`Thời gian còn lại ${formatCountdown(remaining)}`}
        >
          Còn lại {formatCountdown(remaining)}
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={() =>
          end.mutate(session.id, {
            onSuccess: () => toast.success("Đã thoát chế độ xem-như-user"),
            onError: (error) =>
              toast.error(
                error instanceof Error
                  ? error.message
                  : "Không thể thoát chế độ. Vui lòng thử lại.",
              ),
          })
        }
        disabled={end.isPending}
        aria-label="Thoát chế độ xem-như-user"
      >
        Thoát chế độ
      </Button>
    </div>
  );
}
